import React, { useState, useEffect } from "react";
import { Box, Container } from "@mui/material";
import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { FormattedMessage } from "react-intl";

const ELFSIGHT_BOOT_URL = "https://core.service.elfsight.com/p/boot/";
const ELFSIGHT_REVIEWS_URL = "https://service-reviews-ultimate.elfsight.com/data/reviews";

const testimonials = [
  {
    textId: "testimonials_section.cards.1.text",
    nameId: "testimonials_section.cards.1.name",
    roleId: "testimonials_section.cards.1.role",
    typeId: "testimonials_section.types.candidate",
    category: "candidate",
  },
  {
    textId: "testimonials_section.cards.2.text",
    nameId: "testimonials_section.cards.2.name",
    roleId: "testimonials_section.cards.2.role",
    typeId: "testimonials_section.types.candidate",
    category: "candidate",
  },
  {
    textId: "testimonials_section.cards.3.text",
    nameId: "testimonials_section.cards.3.name",
    roleId: "testimonials_section.cards.3.role",
    typeId: "testimonials_section.types.candidate",
    category: "candidate",
  },
];

function TestimonialsSection() {
  const widgetId = process.env.REACT_APP_ELFSIGHT_WIDGET_DESKTOP || null;
  const [rating, setRating] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);

  useEffect(() => {
    if (!widgetId) return;

    const controller = new AbortController();
    const opts = { signal: controller.signal };

    fetch(`${ELFSIGHT_BOOT_URL}?page=${encodeURIComponent(window.location.href)}&w=${widgetId}`, opts)
      .then((res) => res.json())
      .then((boot) => {
        const widget = boot?.data?.widgets?.[widgetId]?.data;
        const token = widget?.public_widget_token;
        const sources = widget?.settings?.sources ?? [];
        const uris = sources.map((s) => s.url).filter(Boolean);

        if (!token || uris.length === 0) return;

        const params = uris.map((uri) => `uris[]=${encodeURIComponent(uri)}`).join("&");

        return fetch(`${ELFSIGHT_REVIEWS_URL}?${params}`, {
          ...opts,
          headers: { Accept: "application/json", "x-widget-token": token },
        });
      })
      .then((res) => res?.json())
      .then((json) => {
        const reviews = json?.result?.data ?? [];
        if (reviews.length > 0) {
          const total = reviews.reduce((sum, r) => sum + (r.rating ?? 0), 0);
          setRating(Math.round((total / reviews.length) * 10) / 10);
          setReviewCount(reviews.length);
        }
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          console.error("Elfsight reviews fetch failed:", err);
        }
      });

    return () => controller.abort();
  }, [widgetId]);

  return (
    <Box
      component="section"
      sx={{
        py: { xs: "3rem", md: "4rem" },
        bgcolor: "background.default",
      }}
    >
      <Container maxWidth="lg" sx={{ maxWidth: "72rem" }}>
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "flex-end" },
            justifyContent: "space-between",
            gap: "1rem",
            mb: "2.5rem",
          }}
        >
          <Box>
            <Box
              component="span"
              sx={{
                display: "block",
                mb: "0.5rem",
                fontSize: "0.875rem",
                fontWeight: 700,
                color: "hsl(153 68% 25%)",
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
              }}
            >
              <FormattedMessage id="testimonials_section.eyebrow" />
            </Box>
            <Box
              component="h2"
              sx={{
                m: 0,
                fontSize: { xs: "1.875rem", md: "2.25rem" },
                fontWeight: 900,
                color: "hsl(178 93% 10%)",
                letterSpacing: "-0.02em",
                fontFamily: "'Segoe UI', 'Work Sans', sans-serif",
              }}
            >
              <FormattedMessage id="testimonials_section.heading" />
            </Box>
          </Box>

          {reviewCount > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "0.375rem",
              }}
            >
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={16}
                  fill="hsl(153 68% 25%)"
                  color="hsl(153 68% 25%)"
                />
              ))}
              <Box
                component="span"
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 700,
                  color: "hsl(178 93% 10%)",
                  ml: "0.25rem",
                  fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                }}
              >
                <FormattedMessage id="testimonials_section.score" values={{ rating }} />
              </Box>
              <Box
                component="span"
                sx={{
                  fontSize: "0.75rem",
                  color: "hsl(178 12% 46%)",
                  fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                }}
              >
                <FormattedMessage id="testimonials_section.review_count" values={{ count: reviewCount }} />
              </Box>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
            gap: "1rem",
          }}
        >
          {testimonials.map((testimonial, i) => (
            <Box
              key={testimonial.nameId}
              component={motion.div}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              sx={{
                bgcolor: "hsl(160 10% 96% / 0.8)",
                borderRadius: "0.75rem",
                p: "1.5rem",
                display: "flex",
                flexDirection: "column",
                transition: "background-color 200ms",
                "&:hover": {
                  bgcolor: "hsl(180 7% 97% / 0.6)",
                },
              }}
            >
              <Quote size={22} color="hsl(153 68% 25% / 0.3)" style={{ marginBottom: "0.75rem" }} />
              <Box
                component="p"
                sx={{
                  m: 0,
                  mb: "1.25rem",
                  flex: 1,
                  color: "hsl(178 93% 10%)",
                  fontSize: "0.875rem",
                  lineHeight: 1.65,
                  fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                }}
              >
                &ldquo;<FormattedMessage id={testimonial.textId} />&rdquo;
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Box
                    component="p"
                    sx={{
                      m: 0,
                      fontWeight: 700,
                      color: "hsl(178 93% 10%)",
                      fontSize: "0.875rem",
                      fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                    }}
                  >
                    <FormattedMessage id={testimonial.nameId} />
                  </Box>
                  <Box
                    component="p"
                    sx={{
                      m: 0,
                      fontSize: "0.75rem",
                      color: "hsl(178 12% 46%)",
                      fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                    }}
                  >
                    <FormattedMessage id={testimonial.roleId} />
                  </Box>
                </Box>

                <Box
                  component="span"
                  sx={{
                    fontSize: "10px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    px: "0.5rem",
                    py: "0.25rem",
                    borderRadius: "0.25rem",
                    fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                    ...(testimonial.category === "candidate"
                      ? { bgcolor: "hsl(153 68% 25% / 0.1)", color: "hsl(153 68% 25%)" }
                      : { bgcolor: "hsl(178 93% 10% / 0.1)", color: "hsl(178 93% 10%)" }),
                  }}
                >
                  <FormattedMessage id={testimonial.typeId} />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
}

export default TestimonialsSection;
